import React from "react";
import { DoorOpen, Sprout, Paintbrush, Share2, Search, BookOpen, Heart, House, Briefcase, Trees, Map, MessageSquare, IdCard, Flower2 } from "lucide-react";
import { FONT, SAFE_BOTTOM, GLASS, glassBox, glassInner } from "../kota/ui";
import { SideDock } from "../kota/SideDock";

// Aksi rumah (dipakai dock bawah & menu radial) — bahasa kaca Jawsel: ikon tinta, aksen biru hanya saat aktif.
export const AKSI = {
  masuk: { label: "Masuk", Icon: DoorOpen },
  kebun: { label: "Kebun", Icon: Sprout },
  hias: { label: "Hias", Icon: Paintbrush },
  halaman: { label: "Halaman", Icon: Flower2 },
  bagikan: { label: "Bagikan", Icon: Share2 },
  istirahat: { label: "Istirahat", Icon: MoonIcon },
  buku_tamu: { label: "Buku tamu", Icon: BookOpen },
  suka: { label: "Suka", Icon: Heart },
  cari: { label: "Cari", Icon: Search },
  rumahku: { label: "Rumahku", Icon: House },
};
function MoonIcon(props) { return <svg viewBox="0 0 24 24" width={props.size || 22} height={props.size || 22} fill="none" stroke="currentColor" strokeWidth={props.strokeWidth || 2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /></svg>; }

export const DOCK_PEMILIK = ["masuk", "kebun", "halaman", "hias", "bagikan", "cari"];
export const DOCK_TAMU = ["masuk", "buku_tamu", "suka", "bagikan", "rumahku"];
// Pintasan dock kanan (bentuk sama dengan dock kanan halaman kota)
export const PINTASAN = [["kerja", Briefcase, "Kerja"], ["alun", Trees, "Alun-alun"], ["kota", Map, "Peta kota"], ["pesan", MessageSquare, "Pesan Warga"], ["ktw", IdCard, "Kartu Warga"]];

// Pembungkus panel bawah kaca (dock, panel Kebun, panel Hias): menempel dasar layar, radius atas 28, lebar maks 560
export function PanelBawah({ children, testid, style }) {
  return (
    <div data-testid={testid} style={{ position: "absolute", left: 0, right: 0, bottom: 0, display: "flex", justifyContent: "center", fontFamily: FONT, pointerEvents: "none", zIndex: 5 }}>
      <div style={{ pointerEvents: "auto", width: "100%", maxWidth: 560, ...glassBox, borderBottom: 0, borderRadius: "28px 28px 0 0", padding: `10px 10px calc(${SAFE_BOTTOM} + 8px)`, boxShadow: GLASS.shadow, ...style }}>
        {children}
      </div>
    </div>
  );
}

// Dock bawah kaca (sama seperti dock kota): ikon tinta + label; aktif = kaca lebih pekat + aksen biru; badge = titik/angka kecil biru
export function RumahDock({ items, onAksi, aktif, badge = {}, disukai }) {
  return (
    <PanelBawah testid="rumah-dock">
        <nav style={{ display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 2 }}>
          {items.map(key => {
            const a = AKSI[key], Icon = a.Icon, on = aktif === key || (key === "suka" && disukai);
            return (
              <button key={key} data-testid={`rumah-dock-${key}`} aria-label={a.label} aria-pressed={on} onClick={() => onAksi(key)}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, borderRadius: 18, cursor: "pointer", padding: "9px 0 7px", minWidth: 0,
                  ...glassInner(on), color: on ? GLASS.accent : GLASS.ink, boxShadow: on ? "0 4px 14px rgba(20,20,20,0.06)" : "none",
                  transition: "background-color .3s, color .3s, transform .12s, box-shadow .18s", WebkitTapHighlightColor: "transparent", fontFamily: FONT }}
                onPointerDown={e => { e.currentTarget.style.transform = "scale(.95)"; }} onPointerUp={e => { e.currentTarget.style.transform = "scale(1)"; }} onPointerLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                <span style={{ position: "relative", width: 26, height: 24, display: "grid", placeItems: "center" }}>
                  <Icon size={22} strokeWidth={1.8} fill={key === "suka" && disukai ? "currentColor" : "none"} />
                  {badge[key] > 0 && <span data-testid={`rumah-badge-${key}`} style={{ position: "absolute", top: -5, right: -9, minWidth: 15, height: 15, padding: "0 4px", borderRadius: 99, background: GLASS.accent, color: "#fff", fontSize: 9.5, fontWeight: 800, display: "grid", placeItems: "center", boxShadow: "0 0 0 2px var(--g-bg-strong)" }}>{badge[key]}</span>}
                </span>
                <span style={{ fontSize: 10, fontWeight: on ? 700 : 600, whiteSpace: "nowrap" }}>{a.label}</span>
              </button>
            );
          })}
        </nav>
        <div style={{ width: 110, height: 4, borderRadius: 99, background: GLASS.handle, margin: "10px auto 0" }} />
    </PanelBawah>
  );
}

// Dock kanan: komponen SideDock kota dengan item pintasan rumah
export function DockKanan({ onNav }) {
  return <SideDock items={PINTASAN} onOpen={k => k && onNav(k)} testid="rumah-dock-kanan" prefix="rumah-pintasan" />;
}

// Menu radial di sekitar rumah (ketuk rumah): tombol kaca bulat melingkar + × di tengah, label pil abu-abu di bawah ikon
export function RadialRumah({ pos, items, onAksi, onClose }) {
  const R = 96, n = items.length;
  const kaca = { ...glassBox, background: GLASS.bgStrong, width: 56, height: 56, borderRadius: 99, color: GLASS.ink, display: "grid", placeItems: "center", cursor: "pointer", padding: 0, position: "absolute", transform: "translate(-50%,-50%)" };
  return (
    <div data-testid="rumah-radial" style={{ position: "absolute", inset: 0, zIndex: 6, fontFamily: FONT }} onClick={onClose}>
      <style>{`@keyframes rm-pop{from{transform:translate(-50%,-50%) scale(.3);opacity:0}to{transform:translate(-50%,-50%) scale(1);opacity:1}}`}</style>
      {items.map((key, i) => {
        const a = AKSI[key], Icon = a.Icon, ang = -Math.PI / 2 + (i / n) * Math.PI * 2, x = pos[0] + Math.cos(ang) * R, y = pos[1] + Math.sin(ang) * R;
        return (
          <button key={key} data-testid={`rumah-radial-${key}`} aria-label={a.label} onClick={e => { e.stopPropagation(); onAksi(key); }}
            style={{ ...kaca, left: x, top: y, animation: `rm-pop .28s cubic-bezier(.2,.9,.3,1.3) ${i * 0.04}s both` }}>
            <Icon size={22} strokeWidth={1.8} />
            <span style={{ position: "absolute", top: 58, left: "50%", transform: "translateX(-50%)", padding: "3px 8px", borderRadius: 99, background: "rgba(38,38,44,.62)", color: "#fff", fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }}>{a.label}</span>
          </button>
        );
      })}
      <button data-testid="rumah-radial-tutup" aria-label="Tutup" onClick={e => { e.stopPropagation(); onClose(); }} style={{ ...kaca, width: 42, height: 42, left: pos[0], top: pos[1], color: GLASS.accent, background: GLASS.accentBg, fontSize: 18, fontWeight: 800 }}>×</button>
    </div>
  );
}
